import {
  ref,
  reactive,
  onBeforeMount,
  getCurrentInstance,
  watch,
  computed
} from "@vue/composition-api";
import router from "@/router";
import store from "@/store";
import useModal from "@/@core/components/base-modal/hooks/useModal";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";
import usePagination from "@/views/components/pagination/usePagination";
import flashdealEntryMappingModel from "@/models/flashdeal/flashdeal-entry-mapping";
import {PRIVILEGE_TYPE} from "@/configs/privilege/privilege"
import useDate from "@/utils/hooks/useDate";

export default function useAddPrivilegeInfo(props) {
  const vm = getCurrentInstance().proxy;

  const { fetchFlashDealEntryMappingDraftList, fetchFlashDealEntryMappingUpdate } = flashdealEntryMappingModel();

  const { openModal, closeModal } = useModal();

  const modalType = reactive({
    editModal: false,
  });

  const tableData = reactive({
    payload: [],
  });

  const { pagination, paginationInfo } = usePagination();
  const { resolveDateWithSeperator, resolveTime } = useDate();

  const {
    openDefaultErrorModal,
    openWarningModal,
    openSuccessModal,
  } = useValidationModal();

  const privilegeEditSelect = reactive({
    campaign : "",
    inventory : "",
    privilegeName : "",
    privilegeImage : "",
    reference : "",
    status : "",
    privilegeType : "",
    point : "",
    newPoint : "",
    validFrom : "",
    validThrough : ""
  });

  const loading = reactive({
    tableData: false,
  });

  const formStatus = reactive({
    validFrom: null,
    validThrough: null,
    status: null,
    inventoryId: null,
    inventoryLabel: null,
  });

  const fields = [{ key: "privilegeBadge", label: "" }];

  const fetchPrivilegeDraftList = (loadingTable = true) => {
    if (loadingTable) {
      loading.tableData = true;
    }
    fetchFlashDealEntryMappingDraftList.payload.Campaign_Alt_Ref =
    router.currentRoute.params.ref;
    fetchFlashDealEntryMappingDraftList.payload.Inventory_Ref = props.privilegeObj.inventoryObj.inventoryId
    fetchFlashDealEntryMappingDraftList.payload.Page = pagination.currentPage;
    fetchFlashDealEntryMappingDraftList.payload.Limit = pagination.perPage;

    store
      .dispatch("api/apiRequest", fetchFlashDealEntryMappingDraftList)
      .then((response) => {
        tableData.payload = response.data.payload;
        pagination.rows = response.data.payload.length;
        pagination.totalRows = response.data.filtered_record;
        vm.$emit('onTotalCount', response.data.filtered_record);
      })
      .catch((error) => {
        openDefaultErrorModal(error);
      })
      .finally(() => {
        loading.tableData = false;
      });
  };

  const onPaginate = () => {
    loading.tableData = true;
    if (tableData.payload.length > 0) {
      const result = props.privilegeObj.payload.slice(
        (pagination.currentPage - 1) * pagination.perPage,
        pagination.currentPage * pagination.perPage
      );
      loading.tableData = false;
      return (tableData.payload = result);
    }
  };

  const canNext = () => {
    return (pagination.currentPage * pagination.perPage) < pagination.totalRows
  }

  const resolveSaveBtnAction = computed(() => {
    return {
      isNext : canNext(),
      text : canNext() ? "Save and Next" : "Save and Finished"
    }
  })

  const onSaveChangeUpdate = () => {
    loading.tableData = true;
    fetchFlashDealEntryMappingUpdate.payload.Campaign_Alt_Ref = router.currentRoute.params.ref;
    fetchFlashDealEntryMappingUpdate.payload.Mapping_Datas = []
    tableData.payload.map(o => {
      fetchFlashDealEntryMappingUpdate.payload.Mapping_Datas.push({
        Inventory_Ref: o.im_inv_ref,
        Privilege_Alt_Ref: o.pv_alt_reference,
        Point: o.criteria_point,
      })
    })
    store.dispatch('api/apiRequest' , fetchFlashDealEntryMappingUpdate).then(response => {
      if(canNext()) {
        loading.tableData = false;
        pagination.currentPage = pagination.currentPage + 1
      }
      else {
        loading.tableData = false;
        openSuccessModal(null,null,null,null,() => {
          vm.$emit('onCloseModal');
        })
      }
    })
    .catch(error => {
      openDefaultErrorModal(error);
      loading.tableData = false;
    })
  }

  const onBackPage = () => {
    pagination.currentPage = pagination.currentPage - 1
  }

  const openEditModal = (obj) => {
    privilegeEditSelect.campaign = obj.campaign_alt_ref
    privilegeEditSelect.inventory = obj.im_inv_ref
    privilegeEditSelect.newPoint = obj.criteria_point
    privilegeEditSelect.point = obj.pv_point_ref
    privilegeEditSelect.privilegeImage = obj.pv_image_url
    privilegeEditSelect.privilegeName = obj.pv_name_th
    privilegeEditSelect.privilegeType = obj.pv_type
    privilegeEditSelect.reference = obj.pv_alt_reference
    privilegeEditSelect.status = resolveStatusLabel(obj.status)
    privilegeEditSelect.validFrom = resolveDateWithSeperator(obj.pv_valid_from)
    privilegeEditSelect.validThrough = resolveDateWithSeperator(obj.pv_valid_through)
    modalType.editModal = true;
    vm.$bvModal.show("flashdeal-edit-privilege");
    openModal();
  };

  const closeEditModal = () => {
    vm.$bvModal.hide("flashdeal-edit-privilege");
  };

  const editUpdatePrivilege = (obj) => {
    tableData.payload.find(o => {
      return o.pv_alt_reference == obj.reference
    }).criteria_point = obj.newPoint;

    closeEditModal();
  }

  const resolveStatusLabel = (status) => {
    switch (status) {
      case 1:
        return "Active";
      case 2:
        return "Inactive";
      case 5:
        return "Draft";
      default:
        return "Unknown";
    }
  };

  const resolveStatusClass = (status) => {
    switch (status) {
      case 1:
        return "active-icon";
      case 2:
        return "inActive-icon";
      case 5:
        return "pending-icon";
      default:
        return "Unknown";
    }
  };

  onBeforeMount(() => {
    fetchPrivilegeDraftList();
  });

  watch(props, () => {
    tableData.payload = props.privilegeObj.payload;
  });

  watch(
    pagination,
    () => {
      if (!loading.tableData) {
        fetchPrivilegeDraftList();
      }
    },
    { deep: true }
  );

  return {
    tableData,
    loading,
    pagination,
    paginationInfo,
    formStatus,
    fields,
    modalType,
    resolveSaveBtnAction,
    privilegeEditSelect,
    PRIVILEGE_TYPE,
    openEditModal,
    closeEditModal,
    onSaveChangeUpdate,
    onBackPage,
    resolveStatusLabel,
    resolveStatusClass,
    editUpdatePrivilege,

  };
}
